import { useCallback, useMemo, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import { INotificationFieldSuggestions, IPageAnalysisError } from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { useCompositeImageLoader } from './use-composite-image-loader'
import { pause } from '../../../_utils/utils'
import { sanitizeKeywords } from '../../../_utils/keywords'

/**
 * Result from a page analysis request.
 * Contains either suggestions (on success) or an error (on failure), never both.
 */
export type PageAnalyzerResult = {
    suggestions: INotificationFieldSuggestions | null
    error: IPageAnalysisError | null
}

/**
 * API surface for the page analyzer hook.
 * Provides an analyze function that returns structured results with error handling.
 */
export type PageAnalyzerApi = {
    readonly isAnalyzing: boolean
    analyze(url: string): Promise<PageAnalyzerResult>
}

type PageAnalysisDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

export const usePageAnalyzer = ({ id: domainId, mediaQuality = 40 }: PageAnalysisDomain): PageAnalyzerApi => {
    const notifSvc = useService(NotificationService)

    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const lastParsedUrlRef = useRef<string | null>(null)
    const resultsRef = useRef<PageAnalyzerResult>({ suggestions: null, error: null })

    const loadCompositeImage = useCompositeImageLoader({ id: domainId, mediaQuality })

    const analyze = useCallback(
        async (url: string): Promise<PageAnalyzerResult> => {
            try {
                if (isAnalyzing) return { suggestions: buildResponse(null), error: null }
                setIsAnalyzing(true)

                if (lastParsedUrlRef.current === url) {
                    await pause(500)
                    return {
                        suggestions: buildResponse(resultsRef.current.suggestions),
                        error: resultsRef.current.error,
                    }
                }

                const res = await notifSvc.analyzeLandingUrl(domainId, url, {
                    showLoadingScreen: false,
                    cancellationKey: 'nfs.analyze',
                })

                const analysisError = res.data?.error ?? null
                if (analysisError) {
                    lastParsedUrlRef.current = url
                    resultsRef.current = { suggestions: null, error: analysisError }
                    return resultsRef.current
                }

                let imageUrl = res.data.imageUrl
                let compositeImage = res.data.compositeImage ?? null
                if (imageUrl) {
                    const loaded = await loadCompositeImage(imageUrl)
                    if (loaded) {
                        imageUrl = loaded.url
                        compositeImage = loaded
                    }
                }

                const data: INotificationFieldSuggestions = {
                    ...res.data,
                    keywords: sanitizeKeywords(res.data.keywords),
                    imageUrl,
                    compositeImage,
                }

                lastParsedUrlRef.current = url
                resultsRef.current = { suggestions: buildResponse(res.ok ? data : null), error: null }

                return resultsRef.current
            } finally {
                setIsAnalyzing(false)
            }
        },
        [domainId, mediaQuality],
    )

    return useMemo(
        () => ({
            analyze,
            isAnalyzing,
        }),
        [analyze, isAnalyzing],
    )
}
